/**
 * Custom hook for managing wizard state
 */

import { useState, useCallback } from 'react';

const INITIAL_FORM_STATE = {
  // Step 1: Basics
  programImage: null,
  name: '',
  category: '',
  sport: 'Soccer',
  gameFormat: '',
  gender: 'Co-ed',
  age: { min: '', max: '' },
  registrationStatus: 'open',
  
  // Step 2: Schedule
  schedule: {
    startDate: '',
    endDate: '',
    frequency: []
  },
  locations: [{ address: '' }],
  
  // Step 3: Pricing
  useSimplePrice: true,
  simplePrice: '',
  cost: {
    registrationFees: [{
      amount: '',
      discount: { percentage: '', validUntil: '' },
      startDate: '',
      endDate: ''
    }],
    feeStructure: [{
      amount: '',
      interval: 'Per Program'
    }]
  },
  
  // Step 4: Details (Optional)
  programDetails: '',
  capacity: {
    maxParticipants: '',
    minParticipants: '',
    waitlistEnabled: false,
    waitlistLimit: '',
    autoEnrollFromWaitlist: true
  },
  staff: [],
  
  // Meta
  active: true
};

export const useWizardState = (initialData = null) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(() => {
    if (initialData) {
      return mergeInitialData(initialData);
    }
    return INITIAL_FORM_STATE;
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Merge initial data for editing
  function mergeInitialData(data) {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };

    return {
      ...INITIAL_FORM_STATE,
      ...data,
      programImage: data.programImage || null,
      registrationStatus: data.registrationStatus || 'open',
      useSimplePrice: data.simplePrice != null,
      simplePrice: data.simplePrice || '',
      schedule: {
        ...INITIAL_FORM_STATE.schedule,
        ...data.schedule,
        startDate: formatDate(data.schedule?.startDate),
        endDate: formatDate(data.schedule?.endDate),
        frequency: data.schedule?.frequency || []
      },
      cost: {
        registrationFees: (data.cost?.registrationFees || []).map(fee => ({
          ...fee,
          startDate: formatDate(fee.startDate),
          endDate: formatDate(fee.endDate),
          discount: {
            ...fee.discount,
            validUntil: formatDate(fee.discount?.validUntil)
          }
        })),
        feeStructure: data.cost?.feeStructure || INITIAL_FORM_STATE.cost.feeStructure
      },
      locations: data.locations?.length > 0 ? data.locations : INITIAL_FORM_STATE.locations,
      age: data.age || INITIAL_FORM_STATE.age,
      capacity: {
        ...INITIAL_FORM_STATE.capacity,
        ...data.capacity
      },
      staff: data.staff || []
    };
  }

  // Update form field
  const updateField = useCallback((field, value) => {
    setFormData(prev => {
      if (field.includes('.')) {
        const parts = field.split('.');
        if (parts.length === 2) {
          const [parent, child] = parts;
          return {
            ...prev,
            [parent]: {
              ...prev[parent],
              [child]: value
            }
          };
        }
        if (parts.length === 3) {
          const [parent, child, grandChild] = parts;
          return {
            ...prev,
            [parent]: {
              ...prev[parent],
              [child]: {
                ...prev[parent][child],
                [grandChild]: value
              }
            }
          };
        }
      }
      return { ...prev, [field]: value };
    });
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  // Update array item
  const updateArrayItem = useCallback((arrayPath, index, field, value) => {
    setFormData(prev => {
      const parts = arrayPath.split('.');
      
      if (parts.length === 1) {
        const newArray = [...prev[arrayPath]];
        newArray[index] = { ...newArray[index], [field]: value };
        return { ...prev, [arrayPath]: newArray };
      }
      
      if (parts.length === 2) {
        const [parent, child] = parts;
        const newArray = [...prev[parent][child]];
        newArray[index] = { ...newArray[index], [field]: value };
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: newArray
          }
        };
      }
      
      return prev;
    });
  }, []);

  // Add array item
  const addArrayItem = useCallback((arrayPath, template) => {
    setFormData(prev => {
      const parts = arrayPath.split('.');
      
      if (parts.length === 1) {
        return { ...prev, [arrayPath]: [...prev[arrayPath], template] };
      }
      
      if (parts.length === 2) {
        const [parent, child] = parts;
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: [...prev[parent][child], template]
          }
        };
      }
      
      return prev;
    });
  }, []);

  // Remove array item
  const removeArrayItem = useCallback((arrayPath, index) => {
    setFormData(prev => {
      const parts = arrayPath.split('.');
      
      if (parts.length === 1) {
        return { ...prev, [arrayPath]: prev[arrayPath].filter((_, i) => i !== index) };
      }
      
      if (parts.length === 2) {
        const [parent, child] = parts;
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: prev[parent][child].filter((_, i) => i !== index)
          }
        };
      }
      
      return prev;
    });
  }, []);

  // Validate step
  const validateStep = useCallback((step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Basics
        if (!formData.name.trim()) newErrors.name = 'Program name is required';
        if (!formData.category) newErrors.category = 'Category is required';
        if (!formData.sport) newErrors.sport = 'Sport is required';
        if (!formData.gameFormat) newErrors.gameFormat = 'Game format is required';
        if (!formData.age.min) newErrors['age.min'] = 'Minimum age is required';
        if (!formData.age.max) newErrors['age.max'] = 'Maximum age is required';
        if (Number(formData.age.min) > Number(formData.age.max)) {
          newErrors['age.max'] = 'Max age must be greater than min age';
        }
        break;

      case 2: // Schedule
        if (!formData.schedule.startDate) newErrors['schedule.startDate'] = 'Start date is required';
        if (!formData.schedule.endDate) newErrors['schedule.endDate'] = 'End date is required';
        if (formData.schedule.startDate && formData.schedule.endDate) {
          if (new Date(formData.schedule.startDate) > new Date(formData.schedule.endDate)) {
            newErrors['schedule.endDate'] = 'End date must be after start date';
          }
        }
        if (!formData.locations[0]?.address?.trim()) {
          newErrors['locations'] = 'At least one location is required';
        }
        if (formData.schedule.frequency.length === 0) {
          newErrors['schedule.frequency'] = 'At least one practice day is required';
        }
        break;

      case 3: // Pricing
        if (formData.useSimplePrice) {
          if (!formData.simplePrice && formData.simplePrice !== 0) {
            newErrors.simplePrice = 'Price is required';
          }
        } else {
          if (formData.cost.registrationFees.length > 0) {
            formData.cost.registrationFees.forEach((fee, index) => {
              if (!fee.amount && fee.amount !== 0) {
                newErrors[`registrationFee.${index}.amount`] = 'Amount is required';
              }
            });
          }
        }
        break;

      case 4: // Details - all optional
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Navigation
  const nextStep = useCallback(() => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      return true;
    }
    return false;
  }, [currentStep, validateStep]);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }, []);

  const goToStep = useCallback((step) => {
    // Only allow going back, or forward if current step is valid
    if (step < currentStep) {
      setCurrentStep(step);
    } else if (step > currentStep && validateStep(currentStep)) {
      setCurrentStep(step);
    }
  }, [currentStep, validateStep]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setCurrentStep(1);
    setErrors({});
  }, []);

  // Get form data for submission
  const getSubmitData = useCallback(() => {
    const data = {
      name: formData.name,
      category: formData.category,
      sport: formData.sport,
      gameFormat: formData.gameFormat,
      gender: formData.gender,
      age: {
        min: Number(formData.age.min),
        max: Number(formData.age.max)
      },
      registrationStatus: formData.registrationStatus,
      schedule: {
        startDate: formData.schedule.startDate,
        endDate: formData.schedule.endDate,
        frequency: formData.schedule.frequency
      },
      locations: formData.locations.filter(loc => loc.address?.trim()),
      programDetails: formData.programDetails || '',
      capacity: {
        maxParticipants: formData.capacity.maxParticipants ? Number(formData.capacity.maxParticipants) : null,
        minParticipants: formData.capacity.minParticipants ? Number(formData.capacity.minParticipants) : 1,
        waitlistEnabled: Boolean(formData.capacity.waitlistEnabled),
        waitlistLimit: formData.capacity.waitlistLimit ? Number(formData.capacity.waitlistLimit) : null,
        autoEnrollFromWaitlist: Boolean(formData.capacity.autoEnrollFromWaitlist)
      },
      staff: formData.staff.filter(s => s.name || s.role),
      active: formData.active
    };

    // Handle pricing
    if (formData.useSimplePrice) {
      data.simplePrice = Number(formData.simplePrice);
      data.cost = { registrationFees: [], feeStructure: [] };
    } else {
      data.simplePrice = null;
      data.cost = {
        registrationFees: formData.cost.registrationFees.map(fee => ({
          amount: Number(fee.amount),
          discount: fee.discount?.percentage ? {
            percentage: Number(fee.discount.percentage),
            validUntil: fee.discount.validUntil || undefined
          } : undefined,
          startDate: fee.startDate,
          endDate: fee.endDate
        })),
        feeStructure: formData.cost.feeStructure.map(fee => ({
          amount: Number(fee.amount),
          interval: fee.interval
        }))
      };
    }

    // Handle program image
    if (formData.programImage) {
      data.programImage = formData.programImage;
    }

    return data;
  }, [formData]);

  return {
    currentStep,
    formData,
    errors,
    isSubmitting,
    setIsSubmitting,
    updateField,
    updateArrayItem,
    addArrayItem,
    removeArrayItem,
    validateStep,
    nextStep,
    prevStep,
    goToStep,
    resetForm,
    getSubmitData
  };
};

export default useWizardState;









